import { defineStore } from 'pinia';
import {
  SUBSCRIPTION,
  SUBSCRIPTION_RELATED_PLANS,
  planDetails,
  SubscriptionPlan,
  REGIONAL_LOCALIZATIONS,
  PlanKey,
} from '@apps-billing-shared/definitions';
import { SUBSCRIPTION_NAME } from '@apps-once/definitions';

const plansWithBillingOptions = [
  SUBSCRIPTION.PREMIUM_YEARLY,
  SUBSCRIPTION.PREMIUM,
  SUBSCRIPTION.GOOD,
  SUBSCRIPTION.GOOD_ANNUAL,
  SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
  SUBSCRIPTION.BETTER,
  SUBSCRIPTION.BETTER_ANNUAL,
  SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
  SUBSCRIPTION.BEST,
  SUBSCRIPTION.BEST_ANNUAL,
  SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY,
];
const plansWithActiveUsers = [SUBSCRIPTION.FLEXI];
const canPurchaseUsers = [
  SUBSCRIPTION.PREMIUM_YEARLY,
  SUBSCRIPTION.GOOD_ANNUAL,
  SUBSCRIPTION.BETTER_ANNUAL,
  SUBSCRIPTION.BEST_ANNUAL,
  SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
  SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
  SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY,
];
const monthlyPlans = [
  SUBSCRIPTION.PREMIUM,
  SUBSCRIPTION.FLEXI,
  SUBSCRIPTION.SCHEDULING_ONLY,
  SUBSCRIPTION.TIMESHEET_ONLY,
  SUBSCRIPTION.GOOD,
  SUBSCRIPTION.BETTER,
  SUBSCRIPTION.BEST,
];
const annualUpfrontPlans = [
  SUBSCRIPTION.PREMIUM_YEARLY,
  SUBSCRIPTION.GOOD_ANNUAL,
  SUBSCRIPTION.BETTER_ANNUAL,
  SUBSCRIPTION.BEST_ANNUAL,
];

// Plan tier groupings
const bestPlans = [
  SUBSCRIPTION.BEST,
  SUBSCRIPTION.BEST_ANNUAL,
  SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY,
];

const betterPlans = [
  SUBSCRIPTION.BETTER,
  SUBSCRIPTION.BETTER_ANNUAL,
  SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
];

const goodPlans = [
  SUBSCRIPTION.GOOD,
  SUBSCRIPTION.GOOD_ANNUAL,
  SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
];

const newPlans = [...bestPlans, ...betterPlans, ...goodPlans];

// Free/trial plans
const freePlans = [
  SUBSCRIPTION.FREE,
  SUBSCRIPTION.PRO_TRIAL,
  SUBSCRIPTION.FREEMIUM,
];

const annualPaidMonthlyPlans = [
  SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
  SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
  SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY,
];

const annualPlans = [...annualUpfrontPlans, ...annualPaidMonthlyPlans];

type PlansStoreState = Record<string, unknown>;

export const usePlansStore = defineStore('plans', {
  state(): PlansStoreState {
    return {};
  },
  getters: {},
  actions: {
    getRelatedPlans(
      planId: SubscriptionPlan
    ): Array<SubscriptionPlan> | undefined {
      return SUBSCRIPTION_RELATED_PLANS[planId];
    },
    getRelatedAnnualPlan(
      planId: SubscriptionPlan
    ): SubscriptionPlan | undefined {
      return this.getRelatedPlans(planId)?.find(relatedPlan =>
        annualPlans.includes(relatedPlan)
      );
    },
    getRelatedMonthlyPlan(
      planId: SubscriptionPlan
    ): SubscriptionPlan | undefined {
      return this.getRelatedPlans(planId)?.find(relatedPlan =>
        monthlyPlans.includes(relatedPlan)
      );
    },
    isAnnual(planId: SubscriptionPlan): boolean {
      return annualPlans.includes(Number(planId));
    },
    isAnnualUpfront(planId: SubscriptionPlan): boolean {
      return annualUpfrontPlans.includes(Number(planId));
    },
    isAnnualPaidMonthly(planId: SubscriptionPlan): boolean {
      return annualPaidMonthlyPlans.includes(Number(planId));
    },
    isMonthly(planId: SubscriptionPlan): boolean {
      return monthlyPlans.includes(Number(planId));
    },
    hasBillingOptions(planId: SubscriptionPlan): boolean {
      return plansWithBillingOptions.includes(Number(planId));
    },
    canPurchaseUsers(planId: SubscriptionPlan): boolean {
      return canPurchaseUsers.includes(Number(planId));
    },
    hasActiveUserBilling(planId: SubscriptionPlan): boolean {
      return plansWithActiveUsers.includes(Number(planId));
    },
    /**
     * Check if a plan is a Best tier plan.
     *
     * @param {SubscriptionPlan} planId - The plan ID to check
     * @return {boolean} True if the plan is a Best tier plan
     */
    isBestPlan(planId: SubscriptionPlan): boolean {
      return bestPlans.includes(Number(planId));
    },
    /**
     * Check if a plan is a Better tier plan.
     *
     * @param {SubscriptionPlan} planId - The plan ID to check
     * @return {boolean} True if the plan is a Better tier plan
     */
    isBetterPlan(planId: SubscriptionPlan): boolean {
      return betterPlans.includes(Number(planId));
    },
    /**
     * Check if a plan is a Good tier plan.
     *
     * @param {SubscriptionPlan} planId - The plan ID to check
     * @return {boolean} True if the plan is a Good tier plan
     */
    isGoodPlan(planId: SubscriptionPlan): boolean {
      return goodPlans.includes(Number(planId));
    },
    /**
     * Check if a plan is a new plan.
     *
     * @param {SubscriptionPlan} planId - The plan ID to check
     * @return {boolean} True if the plan is a new plan
     */
    isNewPlan(planId: SubscriptionPlan): boolean {
      return newPlans.includes(Number(planId));
    },
    /**
     * Check if a plan is a paid plan (not free, trial, or freemium).
     *
     * @param {SubscriptionPlan} planId - The plan ID to check
     * @return {boolean} True if the plan is a paid plan
     */
    isPaidPlan(planId: SubscriptionPlan): boolean {
      return !freePlans.includes(Number(planId));
    },
    /**
     * Get the base plan name for a given plan ID.
     * Returns the parent plan name (e.g., "Best" for "Best Annual").
     *
     * @param {string} planId - The plan ID to get the base name for
     * @return {string} The base plan name or the full plan name as fallback
     */
    getBasePlanName(planId: SubscriptionPlan): string {
      const currentPlanDetails = planDetails[planId];
      if (currentPlanDetails?.parent?.name) {
        return currentPlanDetails.parent.name;
      }

      return SUBSCRIPTION_NAME[planId];
    },
    /**
     * Get the base plan ID (monthly plan) for any given plan ID.
     * Returns the monthly plan ID for the same tier, or the original plan ID if not found.
     *
     * @param {SubscriptionPlan} planId - The plan ID to get the base plan for
     * @return {SubscriptionPlan} The base plan ID (monthly plan) or the original plan ID
     */
    getBasePlanId(planId: SubscriptionPlan): SubscriptionPlan {
      // If this plan is already a monthly plan, return it
      if (this.isMonthly(planId)) {
        return planId;
      }

      // Look through SUBSCRIPTION_RELATED_PLANS to find the base plan
      for (const [basePlanId, relatedPlans] of Object.entries(
        SUBSCRIPTION_RELATED_PLANS
      )) {
        if (relatedPlans.includes(planId)) {
          return parseInt(basePlanId);
        }
      }

      // If not found, return the original plan ID
      return planId;
    },
    /**
     * Get the plan tier below a given plan ID.
     * Returns the plan ID of the tier below, or null if there is no tier below.
     *
     * @param {SubscriptionPlan} planId - The current plan ID
     * @return {SubscriptionPlan | null} The plan ID of the tier below, or null
     */
    getPlanTierBelow(planId: SubscriptionPlan): SubscriptionPlan | null {
      const planTiers = {
        [SUBSCRIPTION.GOOD]: null, // No tier below GOOD
        [SUBSCRIPTION.GOOD_ANNUAL]: null, // No tier below GOOD
        [SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY]: null, // No tier below Good
        [SUBSCRIPTION.BETTER]: SUBSCRIPTION.GOOD,
        [SUBSCRIPTION.BETTER_ANNUAL]: SUBSCRIPTION.GOOD_ANNUAL,
        [SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY]:
          SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY,
        [SUBSCRIPTION.BEST]: SUBSCRIPTION.BETTER,
        [SUBSCRIPTION.BEST_ANNUAL]: SUBSCRIPTION.BETTER_ANNUAL,
        [SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY]:
          SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY,
        // Legacy plans
        [SUBSCRIPTION.PREMIUM]: null, // No tier below PREMIUM
        [SUBSCRIPTION.PREMIUM_YEARLY]: null, // No tier below PREMIUM
        [SUBSCRIPTION.FLEXI]: null, // No tier below FLEXI
        [SUBSCRIPTION.MAXI]: SUBSCRIPTION.PREMIUM,
        [SUBSCRIPTION.ENTERPRISE]: SUBSCRIPTION.MAXI,
      };

      return planTiers[planId] || null;
    },
    /**
     * Get the plan tier name.
     * Returns the display name of the plan tier (e.g., "Core" for BETTER plans, "Pro" for BEST plans).
     *
     * @param {SubscriptionPlan} planId - The plan ID to get the tier name for
     * @return {string} The plan tier name for styling
     */
    getPlanTierName(planId: SubscriptionPlan): string {
      if (this.isGoodPlan(planId)) {
        return SUBSCRIPTION_NAME[SUBSCRIPTION.GOOD];
      } else if (this.isBetterPlan(planId)) {
        return SUBSCRIPTION_NAME[SUBSCRIPTION.BETTER];
      } else if (this.isBestPlan(planId)) {
        return SUBSCRIPTION_NAME[SUBSCRIPTION.BEST];
      }
      return 'unknown';
    },
    /**
     * Get regional plan details including description, features header, and features.
     * Resolves regional-specific content with fallback to common content.
     *
     * @param {PlanKey} planKey - The plan key (lite, core, pro)
     * @param {string} locale - The locale (en-us, en-gb, en-au)
     * @return {object} Object containing description, featuresHeader, and features
     */
    getRegionalPlanDetails(
      planKey: PlanKey,
      locale: string
    ): {
      description: string | undefined;
      featuresHeader: string | undefined;
      features: Record<string, string>;
    } {
      // Map en-uk to en-gb since they share the same localization
      const mappedLocale = locale === 'en-uk' ? 'en-gb' : locale;

      const regional = REGIONAL_LOCALIZATIONS[mappedLocale]?.plans[planKey];
      const fallbackLocale = 'en-au'; // Default fallback
      const fallback = REGIONAL_LOCALIZATIONS[fallbackLocale]?.plans[planKey];

      if (!fallback) {
        throw new Error(
          `Plan ${planKey} not found in fallback locale ${fallbackLocale}`
        );
      }

      return {
        description: regional?.description || fallback.description,
        featuresHeader: regional?.featuresHeader || fallback.featuresHeader,
        features: regional?.features || fallback.features,
      };
    },
    /**
     * Check if a plan is a freemium plan.
     *
     * @param {SubscriptionPlan} planId - The plan ID to check
     * @return {boolean} True if the plan is a freemium plan
     */
    isFreemiumPlan(planId: SubscriptionPlan): boolean {
      return SUBSCRIPTION.FREEMIUM === planId;
    },

    /**
     * Check if the target plan is a higher tier than the current plan.
     * Returns true if the target plan represents an upgrade to a higher tier.
     *
     * @param {SubscriptionPlan} currentPlanId - The current plan ID
     * @param {SubscriptionPlan} targetPlanId - The target plan ID to check
     * @return {boolean} True if target plan is a higher tier than current plan
     */
    isHigherTierPlan(
      currentPlanId: SubscriptionPlan,
      targetPlanId: SubscriptionPlan
    ): boolean {
      // Define plan tier hierarchy (lower number = lower tier)
      const planTierOrder = {
        [SUBSCRIPTION.GOOD]: 1,
        [SUBSCRIPTION.GOOD_ANNUAL]: 1,
        [SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY]: 1,
        [SUBSCRIPTION.BETTER]: 2,
        [SUBSCRIPTION.BETTER_ANNUAL]: 2,
        [SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY]: 2,
        [SUBSCRIPTION.BEST]: 3,
        [SUBSCRIPTION.BEST_ANNUAL]: 3,
        [SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY]: 3,
        // Legacy plans
        [SUBSCRIPTION.PREMIUM]: 1,
        [SUBSCRIPTION.PREMIUM_YEARLY]: 1,
        [SUBSCRIPTION.FLEXI]: 1,
        [SUBSCRIPTION.MAXI]: 2,
        [SUBSCRIPTION.ENTERPRISE]: 3,
      };

      const currentTier = planTierOrder[currentPlanId] || 0;
      const targetTier = planTierOrder[targetPlanId] || 0;

      return targetTier > currentTier;
    },
  },
});
